import React from 'react';
import type { Fetcher } from '../LookupField';
import type { ParameterDefinition, ParameterSection } from './ParameterEditor.types';
import { ParameterEditorDynamicField } from './ParameterEditorDynamicField';
import { getVisibleParameters } from './ParameterEditor.utils';

interface ParameterEditorFieldListProps {
  parameters: ParameterDefinition[];
  sections?: ParameterSection[];
  editedValues: Map<string, string>;
  originalValues: Map<string, string>;
  validationErrors: Map<string, string>;
  onChange: (parameterId: string, value: string) => void;
  onValidationError: (parameterId: string, error: string) => void;
  onEntityDisplayLabel?: (parameterId: string, label: string) => void;
  fetcher: Fetcher;
  readOnly?: boolean;
  onResetToDefault?: (parameterId: string, parameterCode: string) => Promise<void>;
}

const ParameterGrid: React.FC<{
  parameters: ParameterDefinition[];
  editedValues: Map<string, string>;
  originalValues: Map<string, string>;
  validationErrors: Map<string, string>;
  onChange: (parameterId: string, value: string) => void;
  onValidationError: (parameterId: string, error: string) => void;
  onEntityDisplayLabel?: (parameterId: string, label: string) => void;
  fetcher: Fetcher;
  readOnly: boolean;
  onResetToDefault?: (parameterId: string, parameterCode: string) => Promise<void>;
}> = ({
  parameters,
  editedValues,
  originalValues,
  validationErrors,
  onChange,
  onValidationError,
  onEntityDisplayLabel,
  fetcher,
  readOnly,
  onResetToDefault,
}) => {
  const visibleParams = getVisibleParameters(parameters);
  if (visibleParams.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {visibleParams.map((param) => {
        const currentValue = editedValues.has(param.id)
          ? editedValues.get(param.id)!
          : originalValues.get(param.id) ?? '';
        const originalValue = originalValues.get(param.id) ?? '';

        return (
          <ParameterEditorDynamicField
            key={param.id}
            parameter={param}
            value={currentValue}
            originalValue={originalValue}
            onChange={onChange}
            onValidationError={onValidationError}
            onEntityDisplayLabel={onEntityDisplayLabel}
            validationError={validationErrors.get(param.id) || undefined}
            fetcher={fetcher}
            readOnly={readOnly}
            onResetToDefault={onResetToDefault}
          />
        );
      })}
    </div>
  );
};

export const ParameterEditorFieldList: React.FC<ParameterEditorFieldListProps> = ({
  parameters,
  sections,
  editedValues,
  originalValues,
  validationErrors,
  onChange,
  onValidationError,
  onEntityDisplayLabel,
  fetcher,
  readOnly = false,
  onResetToDefault,
}) => {
  const visibleDirectParams = getVisibleParameters(parameters);
  const sortedSections = (sections ?? [])
    .filter((s) => getVisibleParameters(s.parameters).length > 0)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const hasContent = visibleDirectParams.length > 0 || sortedSections.length > 0;

  if (!hasContent) {
    return (
      <div className="flex items-center justify-center py-12 text-content-secondary dark:text-dark-content-secondary text-sm">
        No se encontraron parametros para mostrar.
      </div>
    );
  }

  const gridProps = {
    editedValues,
    originalValues,
    validationErrors,
    onChange,
    onValidationError,
    onEntityDisplayLabel,
    fetcher,
    readOnly,
    onResetToDefault,
  };

  return (
    <div className="p-4 space-y-6">
      {visibleDirectParams.length > 0 && (
        <ParameterGrid parameters={parameters} {...gridProps} />
      )}

      {sortedSections.map((section) => (
        <div key={section.id}>
          <h3 className="text-xs font-semibold text-content-tertiary dark:text-dark-content-tertiary uppercase tracking-wider mb-3 pb-2 border-b border-border-primary dark:border-dark-border-primary">
            {section.name}
          </h3>
          <ParameterGrid parameters={section.parameters} {...gridProps} />
        </div>
      ))}
    </div>
  );
};
